"use client";

import { cloneElement, type MouseEventHandler, type ReactElement } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowDown,
  ArrowUp,
  Ban,
  CheckCheck,
  CheckCircle,
  CircleDollarSign,
  CircleX,
  ExternalLink,
  Eye,
  Loader2,
  MessageSquareReply,
  PackagePlus,
  Pencil,
  PencilLine,
  RotateCcw,
  Save,
  Send,
  Trash2,
  Truck,
  UserMinus,
  XCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";

export const ADMIN_TABLE_ICON_ACTIONS_INNER_CLASS =
  "flex w-fit shrink-0 items-center gap-1";

export type AdminTableIconActionTone = "primary" | "destructive" | "neutral";
export type AdminTableIconActionSize = "default" | "sm";

const iconButtonClass =
  "inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors disabled:pointer-events-none disabled:opacity-50";

const toneClass: Record<AdminTableIconActionTone, string> = {
  primary: "hover:bg-muted hover:text-primary",
  destructive: "hover:bg-muted hover:text-destructive",
  neutral: "hover:bg-muted hover:text-foreground",
};

export function AdminTableIconActionButton({
  label,
  icon: Icon,
  onClick,
  disabled = false,
  saving = false,
  tone = "neutral",
  size = "default",
  className,
  render,
}: {
  label: string;
  icon: LucideIcon;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  saving?: boolean;
  tone?: AdminTableIconActionTone;
  size?: AdminTableIconActionSize;
  className?: string;
  render?: ReactElement<{
    className?: string;
    children?: React.ReactNode;
    "aria-label"?: string;
  }>;
}) {
  const iconSizeClass = size === "sm" ? "size-3.5" : "size-4";
  const content = saving ? (
    <Loader2 className={cn(iconSizeClass, "animate-spin")} aria-hidden />
  ) : (
    <Icon className={iconSizeClass} />
  );

  const sharedClass = cn(iconButtonClass, size === "sm" && "size-7", toneClass[tone], className);

  if (render) {
    return cloneElement(render, {
      "aria-label": label,
      className: cn(sharedClass, render.props.className),
      children: content,
    } as Partial<typeof render.props>);
  }

  return (
    <button
      type="button"
      data-size={size}
      aria-label={label}
      aria-busy={saving || undefined}
      disabled={disabled || saving}
      className={sharedClass}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

export function AdminTableIconEditButton({
  label,
  onClick,
  disabled,
  saving,
  className,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  saving?: boolean;
  className?: string;
}) {
  return (
    <AdminTableIconActionButton
      label={label}
      icon={Pencil}
      tone="primary"
      disabled={disabled}
      saving={saving}
      className={className}
      onClick={onClick}
    />
  );
}

export function AdminTableIconDeleteButton({
  label,
  onClick,
  disabled,
  saving,
  className,
  size,
}: {
  label: string;
  onClick: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  saving?: boolean;
  className?: string;
  size?: AdminTableIconActionSize;
}) {
  return (
    <AdminTableIconActionButton
      label={label}
      icon={Trash2}
      tone="destructive"
      size={size}
      disabled={disabled}
      saving={saving}
      className={className}
      onClick={onClick}
    />
  );
}

export function AdminTableIconSaveButton({
  label = "保存",
  onClick,
  disabled = false,
  saving = false,
  active = true,
  requireActive = true,
  className,
}: {
  label?: string;
  onClick: () => void;
  disabled?: boolean;
  saving?: boolean;
  active?: boolean;
  requireActive?: boolean;
  className?: string;
}) {
  return (
    <AdminTableIconActionButton
      label={label}
      icon={Save}
      tone="primary"
      disabled={disabled || (requireActive && !active)}
      saving={saving}
      className={className}
      onClick={onClick}
    />
  );
}

export function AdminTableIconViewButton({
  label,
  onClick,
  disabled,
  className,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <AdminTableIconActionButton
      label={label}
      icon={Eye}
      tone="neutral"
      disabled={disabled}
      onClick={onClick}
      className={className}
    />
  );
}

export function AdminTableIconLinkButton({
  label,
  href,
  target = "_blank",
  rel = "nofollow",
  className,
}: {
  label: string;
  href: string;
  target?: string;
  rel?: string;
  className?: string;
}) {
  return (
    <a
      href={href}
      target={target}
      rel={rel}
      aria-label={label}
      className={cn(iconButtonClass, toneClass.primary, className)}
    >
      <ExternalLink className="size-4" />
    </a>
  );
}

export const AdminTableActionIcons = {
  save: Save,
  view: Eye,
  approve: CheckCircle,
  reject: XCircle,
  reply: MessageSquareReply,
  ship: Truck,
  reship: PackagePlus,
  complete: CheckCheck,
  cancel: Ban,
  send: Send,
  rename: PencilLine,
  remove: UserMinus,
  close: CircleX,
  refund: RotateCcw,
  settle: CircleDollarSign,
  moveUp: ArrowUp,
  moveDown: ArrowDown,
} as const;
